import React from 'react'
import PropTypes from 'prop-types'
import { Badge, Button } from 'react-bootstrap'
import * as FaIcons from 'react-icons/fa'

const CierreTable = ({
    cierres,
    loading,
    error,
    pagination,
    refreshingCierreId,
    generatingPdfCierreId,
    onPageChange,
    onRefresh,
    onGeneratePdf,
    onSendEmails,
    canCreate,
    canRefresh,
    canGeneratePdf,
    onShowCreateModal,
    canSendEmail
}) => {
    // Obtener clase de badge según estado
    const getEstadoBadgeClass = estado => {
        switch (estado) {
            case 'Creado':
                return 'success'
            case 'PDF Generado':
                return 'success'
            case 'PDFs Creados':
                return 'success'
            case 'Correos Enviados':
                return 'success'
            case 'Procesando':
                return 'warning'
            case 'Enviando Correos':
                return 'warning'
            case 'Generando PDF':
                return 'warning'
            case 'Error':
                return 'danger'
            case 'Error Correos':
                return 'danger'
            case 'Completado':
                return 'primary'
            default:
                return 'secondary'
        }
    }

    // Formatear fecha en formato dd-mm-yyyy
    const formatFecha = fecha => {
        if (!fecha) return ''
        // La fecha puede llegar como "yyyy-mm-ddT..." o "yyyy-mm-dd"
        const datePart = fecha.split('T')[0]
        const [year, month, day] = datePart.split('-')
        return `${day}-${month}-${year}`
    }

    // Formatear fecha con hora
    const formatFechaHora = fecha => {
        if (!fecha) return ''
        return new Date(fecha).toLocaleString('es-CL')
    }

    // Renderizar cabecera
    const renderHeader = () => (
        <thead className="table-light">
            <tr>
                <th>Cierre</th>
                <th>Rango de Fechas</th>
                <th>Fecha Creación</th>
                <th>Creado por</th>
                <th>Archivos</th>
                <th>Estado</th>
                <th>Rutas</th>
                <th>Acciones</th>
            </tr>
        </thead>
    )

    // Renderizar filas
    const renderRows = () => (
        <tbody>
            {cierres.map(cierre => (
                <tr key={cierre.id}>
                    <td>
                        <strong>{cierre.cierre}</strong>
                    </td>
                    <td>
                        {formatFecha(cierre.fecha_inicio)} - {formatFecha(cierre.fecha_termino)}
                    </td>
                    <td>{formatFechaHora(cierre.createdDate)}</td>
                    <td>{cierre.createdBy}</td>
                    <td>
                        <div className="d-flex gap-2 align-items-center">
                            {/* Archivo Excel */}
                            {cierre.drive ? (
                                <a
                                    href={cierre.drive}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-success"
                                    title="Ver archivo Excel en Google Drive">
                                    <FaIcons.FaGoogleDrive size={20} />
                                </a>
                            ) : (
                                <span className="text-muted" title="Archivo Excel no disponible">
                                    <FaIcons.FaFileExcel size={16} />
                                </span>
                            )}

                            {/* Archivo PDF si existe */}
                            {cierre.pdf && (
                                <a
                                    href={cierre.pdf}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-danger"
                                    title="Descargar archivo PDF">
                                    <FaIcons.FaFilePdf size={16} />
                                </a>
                            )}
                        </div>
                    </td>
                    <td>
                        <Badge
                            bg={getEstadoBadgeClass(cierre.estado_actual?.estado)}
                            className="d-flex align-items-center gap-1">
                            {(cierre.estado_actual?.estado === 'Procesando' ||
                                cierre.estado_actual?.estado === 'Generando PDF' ||
                                cierre.estado_actual?.estado === 'Enviando Correos') && (
                                    <div
                                        className="spinner-border spinner-border-sm"
                                        style={{ width: '12px', height: '12px' }}
                                        role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                )}
                            {cierre.estado_actual?.estado || 'Sin estado'}
                        </Badge>
                    </td>
                    <td>
                        <span className="badge bg-info">{cierre.estadisticas?.total_rutas || 0}</span>
                    </td>
                    <td>
                        <div className="d-flex gap-1">
                            {/* Botón Refresh */}
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => onRefresh(cierre.id)}
                                disabled={
                                    loading ||
                                    refreshingCierreId === cierre.id ||
                                    generatingPdfCierreId === cierre.id ||
                                    !canRefresh
                                }
                                title="Actualizar cierre"
                                style={{
                                    width: '32px',
                                    height: '32px',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                {refreshingCierreId === cierre.id ? (
                                    <div
                                        className="spinner-border spinner-border-sm"
                                        style={{ width: '12px', height: '12px' }}
                                        role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                ) : (
                                    <FaIcons.FaSync size={12} />
                                )}
                            </Button>

                            {/* Botón Generar PDF */}
                            {canGeneratePdf && (
                                <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => onGeneratePdf(cierre.id)}
                                    disabled={
                                        loading ||
                                        refreshingCierreId === cierre.id ||
                                        generatingPdfCierreId === cierre.id
                                    }
                                    title="Generar PDF"
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        padding: '0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                    {generatingPdfCierreId === cierre.id ? (
                                        <div
                                            className="spinner-border spinner-border-sm"
                                            style={{ width: '12px', height: '12px' }}
                                            role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    ) : (
                                        <FaIcons.FaFilePdf size={12} />
                                    )}
                                </Button>
                            )}
                            {/* Botón enviar correos */}
                            {canSendEmail && (
                                <Button
                                    variant="outline-warning"
                                    size="sm"
                                    onClick={() => onSendEmails(cierre.id)}
                                    disabled={
                                        loading ||
                                        refreshingCierreId === cierre.id ||
                                        generatingPdfCierreId === cierre.id
                                    }
                                    title="Enviar correos"
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        padding: '0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                    <FaIcons.FaEnvelope size={12} />
                                </Button>
                            )}
                        </div>
                    </td>
                </tr>
            ))}
        </tbody>
    )

    // Renderizar mensaje cuando no hay datos
    const renderEmptyState = () => (
        <tbody>
            <tr>
                <td colSpan="8" className="text-center py-4">
                    <div className="d-flex flex-column align-items-center">
                        <FaIcons.FaFileExcel size={48} className="text-muted mb-2" />
                        <p className="text-muted mb-0">No se encontraron cierres</p>
                    </div>
                </td>
            </tr>
        </tbody>
    )

    // Renderizar paginación
    const renderPagination = () => (
        <div className="d-flex justify-content-between align-items-center mt-3 px-3 py-2">
            <div className="d-flex align-items-center gap-2">
                <button
                    className="btn-outline-primary btn-sm"
                    onClick={() => onPageChange(1)}
                    disabled={pagination.currentPage === 1 || loading}>
                    <FaIcons.FaAngleDoubleLeft />
                </button>
                <button
                    className="btn-outline-primary btn-sm"
                    onClick={() => onPageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1 || loading}>
                    <FaIcons.FaAngleLeft />
                </button>
                <span className="px-3" style={{ minWidth: '150px', textAlign: 'center', fontSize: '0.9rem' }}>
                    Página <strong>{pagination.currentPage}</strong> de <strong>{pagination.totalPages}</strong>
                </span>
                <button
                    className="btn-outline-primary btn-sm"
                    onClick={() => onPageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage >= pagination.totalPages || loading}>
                    <FaIcons.FaAngleRight />
                </button>
                <button
                    className="btn-outline-primary btn-sm"
                    onClick={() => onPageChange(pagination.totalPages)}
                    disabled={pagination.currentPage >= pagination.totalPages || loading}>
                    <FaIcons.FaAngleDoubleRight />
                </button>
            </div>
            <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                    Total registros: <strong style={{ color: '#212529' }}>{pagination.totalItems}</strong>
                </span>
            </div>
        </div>
    )

    return (
        <div>
            {/* Header con botón de crear */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Cierres Mensuales</h5>
            </div>

            {/* Mostrar error si existe */}
            {error && (
                <div className="alert alert-danger mb-3">
                    <FaIcons.FaExclamationTriangle className="me-2" />
                    {error}
                </div>
            )}

            <div className="table-responsive" style={{ position: 'relative', minHeight: '300px' }}>
                {/* Overlay de loading */}
                {loading && (
                    <div 
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10
                        }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                <span className="visually-hidden">Cargando...</span>
                            </div>
                            <p className="mt-2 text-muted">Cargando cierres...</p>
                        </div>
                    </div>
                )}
                
                <table className="table table-hover">
                    {renderHeader()}
                    {cierres.length > 0 ? renderRows() : renderEmptyState()}
                </table>
            </div>

            {pagination && pagination.totalItems > 0 && renderPagination()}
        </div>
    )
}

CierreTable.propTypes = {
    cierres: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    error: PropTypes.string,
    pagination: PropTypes.shape({
        currentPage: PropTypes.number.isRequired,
        totalPages: PropTypes.number.isRequired,
        totalItems: PropTypes.number.isRequired,
        pageSize: PropTypes.number.isRequired
    }),
    refreshingCierreId: PropTypes.string,
    generatingPdfCierreId: PropTypes.string,
    onPageChange: PropTypes.func.isRequired,
    onRefresh: PropTypes.func.isRequired,
    onGeneratePdf: PropTypes.func.isRequired,
    onSendEmails: PropTypes.func.isRequired,
    canCreate: PropTypes.bool,
    canRefresh: PropTypes.bool,
    canGeneratePdf: PropTypes.bool,
    canSendEmail: PropTypes.bool,
    onShowCreateModal: PropTypes.func.isRequired
}

CierreTable.defaultProps = {
    loading: false,
    error: null,
    refreshingCierreId: null,
    generatingPdfCierreId: null,
    pagination: null,
    canCreate: false,
    canRefresh: false,
    canGeneratePdf: false,
    canSendEmail: false
}

export default CierreTable
